// Public Roadmap Page - Visual feature roadmap with Kanban and Timeline views
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  IconButton,
  Avatar,
  AvatarGroup,
  LinearProgress,
  Tooltip,
  Paper,
  alpha,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  ThumbUp,
  ThumbUpOutlined,
  CheckCircle,
  Schedule,
  Lightbulb,
  ViewKanban,
  Timeline,
  TrendingUp,
} from '@mui/icons-material';
import { usePortal } from './PortalLayout';
import { featuresService } from '@/services/collections/features';
import type { FeatureRequest, FeatureStatus } from '@/types';

const ROADMAP_COLUMNS: { status: FeatureStatus; label: string; icon: React.ReactNode; color: string }[] = [
  { status: 'under_review', label: 'Under Review', icon: <Lightbulb />, color: '#94a3b8' },
  { status: 'planned', label: 'Planned', icon: <Schedule />, color: '#3b82f6' },
  { status: 'in_progress', label: 'In Progress', icon: <TrendingUp />, color: '#f59e0b' },
  { status: 'released', label: 'Released', icon: <CheckCircle />, color: '#10b981' },
];

// Timeline quarters for the next 12 months
const getTimelineQuarters = () => {
  const now = new Date();
  const quarters = [];
  for (let i = 0; i < 5; i++) {
    const quarterDate = new Date(now.getFullYear(), now.getMonth() + i * 3, 1);
    const q = Math.floor(quarterDate.getMonth() / 3) + 1;
    quarters.push({
      label: `Q${q} ${quarterDate.getFullYear()}`,
      value: `Q${q} ${quarterDate.getFullYear()}`,
      isPast: quarterDate < now,
    });
  }
  return quarters;
};

export const RoadmapPage = () => {
  const navigate = useNavigate();
  const { organization, project, branding } = usePortal();

  const [features, setFeatures] = useState<FeatureRequest[]>([]);
  const [votedIds, setVotedIds] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'kanban' | 'timeline'>('kanban');

  const timelineQuarters = getTimelineQuarters();

  // Load voted features from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(`voted_${organization?.id}`);
    if (stored) {
      setVotedIds(JSON.parse(stored));
    }
  }, [organization?.id]);

  // Fetch features
  useEffect(() => {
    const fetchFeatures = async () => {
      if (!organization || !project) return;

      try {
        const data = await featuresService.getPublic(organization.id, project.id);
        setFeatures(data);
      } catch (error) {
        console.error('Error fetching roadmap:', error);
      }
    };

    fetchFeatures();
  }, [organization, project]);

  // Handle vote
  const handleVote = async (e: React.MouseEvent, feature: FeatureRequest) => {
    e.stopPropagation();
    const visitorId = getVisitorId();
    const hasVoted = votedIds.includes(feature.id);

    try {
      if (hasVoted) {
        await featuresService.unvote(feature.id, visitorId);
        setVotedIds((prev) => prev.filter((id) => id !== feature.id));
        setFeatures((prev) =>
          prev.map((f) =>
            f.id === feature.id ? { ...f, voteCount: f.voteCount - 1 } : f
          )
        );
      } else {
        await featuresService.vote(feature.id, visitorId);
        setVotedIds((prev) => [...prev, feature.id]);
        setFeatures((prev) =>
          prev.map((f) =>
            f.id === feature.id ? { ...f, voteCount: f.voteCount + 1 } : f
          )
        );
      }

      // Save to localStorage
      const newVotedIds = hasVoted
        ? votedIds.filter((id) => id !== feature.id)
        : [...votedIds, feature.id];
      localStorage.setItem(`voted_${organization?.id}`, JSON.stringify(newVotedIds));
    } catch (error) {
      console.error('Error voting:', error);
    }
  };

  // Get or create visitor ID
  const getVisitorId = (): string => {
    let visitorId = localStorage.getItem('visitor_id');
    if (!visitorId) {
      visitorId = `visitor_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem('visitor_id', visitorId);
    }
    return visitorId;
  };

  // Group features by status
  const featuresByStatus = ROADMAP_COLUMNS.reduce((acc, col) => {
    acc[col.status] = features.filter((f) => f.status === col.status);
    return acc;
  }, {} as Record<FeatureStatus, FeatureRequest[]>);

  // Group features by planned release for timeline
  const featuresByQuarter = timelineQuarters.reduce((acc, quarter) => {
    acc[quarter.value] = features.filter((f) =>
      f.plannedRelease === quarter.value &&
      ['planned', 'in_progress'].includes(f.status)
    );
    return acc;
  }, {} as Record<string, FeatureRequest[]>);

  // Features without planned release
  const unscheduledFeatures = features.filter(
    (f) => !f.plannedRelease && ['planned', 'in_progress'].includes(f.status)
  );

  // Get max votes for progress
  const maxVotes = Math.max(...features.map((f) => f.voteCount), 1);

  // Feature Card Component
  const FeatureCard = ({ feature, color }: { feature: FeatureRequest; color: string }) => (
    <Card
      sx={{
        cursor: 'pointer',
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 2,
        },
      }}
      onClick={() => navigate(`/p/${organization?.slug}/ideas/${feature.id}`)}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
          {feature.title}
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            mb: 1.5,
          }}
        >
          {feature.description}
        </Typography>

        {/* Vote Bar */}
        <Box sx={{ mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={(feature.voteCount / maxVotes) * 100}
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: alpha(color, 0.2),
              '& .MuiLinearProgress-bar': {
                bgcolor: color,
              },
            }}
          />
        </Box>

        {/* Vote Button & Count */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Tooltip title={votedIds.includes(feature.id) ? 'Remove vote' : 'Vote for this'}>
            <IconButton
              size="small"
              onClick={(e) => handleVote(e, feature)}
              sx={{
                color: votedIds.includes(feature.id) ? branding.primaryColor : 'text.secondary',
              }}
            >
              {votedIds.includes(feature.id) ? (
                <ThumbUp fontSize="small" />
              ) : (
                <ThumbUpOutlined fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Typography variant="caption" fontWeight="bold">
            {feature.voteCount} votes
          </Typography>

          {feature.voterIds?.length > 0 && (
            <AvatarGroup
              max={3}
              sx={{
                '& .MuiAvatar-root': {
                  width: 20,
                  height: 20,
                  fontSize: 10,
                },
              }}
            >
              {feature.voterIds.slice(0, 3).map((id, i) => (
                <Avatar key={i}>{id[0]?.toUpperCase()}</Avatar>
              ))}
            </AvatarGroup>
          )}
        </Box>

        {/* Status chip for timeline view */}
        {viewMode === 'timeline' && (
          <Chip
            label={ROADMAP_COLUMNS.find((c) => c.status === feature.status)?.label}
            size="small"
            sx={{
              mt: 1,
              bgcolor: alpha(color, 0.1),
              color: color,
            }}
          />
        )}

        {/* Planned Release for kanban view */}
        {viewMode === 'kanban' && feature.plannedRelease && (
          <Chip
            label={feature.plannedRelease}
            size="small"
            variant="outlined"
            sx={{ mt: 1 }}
          />
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Product Roadmap
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          See what we're working on and vote for features you'd like to see
        </Typography>

        {/* View Toggle */}
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
        >
          <ToggleButton value="kanban">
            <ViewKanban sx={{ mr: 0.5 }} /> Kanban
          </ToggleButton>
          <ToggleButton value="timeline">
            <Timeline sx={{ mr: 0.5 }} /> Timeline
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(4, 1fr)',
            },
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          {ROADMAP_COLUMNS.map((column) => (
            <Paper
              key={column.status}
              elevation={0}
              sx={{
                bgcolor: alpha(column.color, 0.05),
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {/* Column Header */}
              <Box
                sx={{
                  bgcolor: column.color,
                  color: 'white',
                  py: 1.5,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {column.icon}
                <Typography fontWeight="bold">{column.label}</Typography>
                <Chip
                  label={featuresByStatus[column.status]?.length || 0}
                  size="small"
                  sx={{
                    ml: 'auto',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                  }}
                />
              </Box>

              {/* Features */}
              <Stack spacing={1.5} sx={{ p: 1.5 }}>
                {featuresByStatus[column.status]?.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: 'center', py: 4 }}
                  >
                    No items yet
                  </Typography>
                ) : (
                  featuresByStatus[column.status]?.map((feature) => (
                    <FeatureCard key={feature.id} feature={feature} color={column.color} />
                  ))
                )}
              </Stack>
            </Paper>
          ))}
        </Box>
      )}

      {/* Timeline View */}
      {viewMode === 'timeline' && (
        <Box sx={{ overflowX: 'auto', pb: 2 }}>
          <Box sx={{ minWidth: { xs: timelineQuarters.length * 220, md: '100%' } }}>
            {/* Timeline Header */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 0,
                borderRadius: '8px 8px 0 0',
                bgcolor: 'grey.50',
              }}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${timelineQuarters.length}, 1fr)`, gap: 1 }}>
                {timelineQuarters.map((quarter, index) => (
                  <Box
                    key={quarter.value}
                    sx={{
                      textAlign: 'center',
                      position: 'relative',
                      '&::after': index < timelineQuarters.length - 1 ? {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 1,
                        height: 24,
                        bgcolor: 'grey.300',
                      } : undefined,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{
                        color: quarter.isPast ? 'text.secondary' : branding.primaryColor,
                      }}
                    >
                      {quarter.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {featuresByQuarter[quarter.value]?.length || 0} features
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>

            {/* Timeline Line */}
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${timelineQuarters.length}, 1fr)`, px: 2 }}>
              {timelineQuarters.map((quarter) => (
                <Box
                  key={quarter.value}
                  sx={{
                    height: 4,
                    bgcolor: quarter.isPast ? 'grey.300' : branding.primaryColor,
                    position: 'relative',
                    my: 2,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: quarter.isPast ? 'grey.400' : branding.primaryColor,
                      border: '2px solid white',
                      boxShadow: 1,
                      zIndex: 1,
                    },
                  }}
                />
              ))}
            </Box>

            {/* Timeline Content */}
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${timelineQuarters.length}, 1fr)`, gap: 2 }}>
              {timelineQuarters.map((quarter) => (
                <Box key={quarter.value}>
                  <Stack spacing={1.5}>
                    {featuresByQuarter[quarter.value]?.length === 0 ? (
                      <Paper
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          bgcolor: 'grey.50',
                          border: '2px dashed',
                          borderColor: 'grey.300',
                          borderRadius: 2,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No features scheduled
                        </Typography>
                      </Paper>
                    ) : (
                      featuresByQuarter[quarter.value]?.map((feature) => {
                        const statusConfig = ROADMAP_COLUMNS.find((c) => c.status === feature.status);
                        return (
                          <FeatureCard
                            key={feature.id}
                            feature={feature}
                            color={statusConfig?.color || '#94a3b8'}
                          />
                        );
                      })
                    )}
                  </Stack>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Unscheduled Features */}
          {unscheduledFeatures.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" fontWeight="medium" gutterBottom>
                Coming Soon (Not Yet Scheduled)
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                  },
                  gap: 2,
                }}
              >
                {unscheduledFeatures.map((feature) => {
                  const statusConfig = ROADMAP_COLUMNS.find((c) => c.status === feature.status);
                  return (
                    <FeatureCard
                      key={feature.id}
                      feature={feature}
                      color={statusConfig?.color || '#94a3b8'}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Recently Released */}
      {features.filter((f) => f.status === 'released' && f.releaseNotes).length > 0 && (
        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Recently Released
          </Typography>
          <Stack spacing={2}>
            {features
              .filter((f) => f.status === 'released' && f.releaseNotes)
              .slice(0, 5)
              .map((feature) => (
                <Card
                  key={feature.id}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 2 },
                  }}
                  onClick={() => navigate(`/p/${organization?.slug}/ideas/${feature.id}`)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <CheckCircle color="success" />
                      <Box>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feature.releaseNotes}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};
